import { Appointment } from '@chiroup/core/types/Appointment.type';
import { useInfiniteQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import locationService from '../services/location.service';

const PAGE_SIZE = 10;

type PastAppointmentsPage = {
  items: Appointment[];
  hasMore: boolean;
};

/**
 * Server-paginated history of past appointments. Each `fetchNextPage`
 * call hits `GET /appointment/{path}?past=true&offset=N&limit=10` so we
 * never pull more than one page from the DB at a time. The wrapper hook
 * is intentionally infinite-query-shaped — Appointments.tsx flattens
 * `data.pages` to render and uses `hasNextPage` / `fetchNextPage` to
 * drive the "Show more" button.
 *
 * `patientId` (optional) scopes the response to a single patient.
 * Family accounts share an email/phone, so without scoping the list
 * would mix every household member's history together.
 */
const usePastAppointments = (overridePath?: string | null, patientId?: string | null) => {
  const { path: urlPath } = useParams();
  const path = overridePath || urlPath;

  return useInfiniteQuery<PastAppointmentsPage>(
    ['pastAppointments', path, patientId || null],
    ({ pageParam = 0 }) => {
      if (!path) {
        return Promise.resolve({ items: [], hasMore: false });
      }

      return locationService.getPastAppointments(path, {
        offset: pageParam,
        limit: PAGE_SIZE,
        patientId,
      });
    },
    {
      enabled: !!path,
      refetchOnWindowFocus: false,
      retry: false,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length * PAGE_SIZE : undefined,
    }
  );
};

export default usePastAppointments;
export { PAGE_SIZE as PAST_PAGE_SIZE };
